// CMS API Client Service - Secure Proxy

import type {
  CMSProductDetails,
  CMSProductsResponse,
  CMSCategory,
  CMSSiteSettings,
  CMSReviewsResponse,
  CMSSearchResult,
  CMSSite,
  CMSBrand,
} from "@/types/cms";
import { supabase } from "@/integrations/supabase/client";
import { getSiteSlug } from "@/lib/siteSlug";

async function fetchFromCMS<T>(endpoint: string, method: 'GET' | 'POST' = 'GET', params: Record<string, string> = {}, body?: any): Promise<T> {
  // Add site parameter if not already present (dynamically from domain)
  if (!params.site) {
    params.site = getSiteSlug();
  }

  // Call local cms-proxy edge function with JWT authentication
  const { data, error } = await supabase.functions.invoke('cms-proxy', {
    body: {
      endpoint,
      method,
      params,
      body,
    },
  });

  if (error) {
    console.error(`CMS Proxy Error:`, error);
    throw new Error(`CMS API request failed: ${error.message}`);
  }

  return data as T;
}

export const cmsApi = {
  // Supplements
  async getSupplements(params?: {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
  }): Promise<CMSProductsResponse> {
    const queryParams: Record<string, string> = {};
    if (params?.page) queryParams.page = params.page.toString();
    if (params?.limit) queryParams.limit = params.limit.toString();
    if (params?.category) queryParams.category = params.category;
    if (params?.search) queryParams.search = params.search;

    const response = await fetchFromCMS<{ supplements: any[], pagination: any }>('api-supplements', 'GET', queryParams);
    
    // Transform 'supplements' key to 'products' for backwards compatibility
    return {
      products: response.supplements || [],
      pagination: response.pagination
    };
  },

  async getSupplement(slug: string): Promise<CMSProductDetails> {
    const response = await fetchFromCMS<{ supplement: any, images: any[], reviews: any[], bundles: any[], biomarkers: any[] }>('api-supplement', 'GET', { slug });
    
    // Transform 'supplement' key to 'product' for backwards compatibility
    return {
      product: response.supplement,
      images: response.images || [],
      reviews: response.reviews || [],
      bundles: response.bundles || [],
      biomarkers: response.biomarkers || []
    };
  },

  async searchProducts(query: string): Promise<CMSSearchResult> {
    return fetchFromCMS<CMSSearchResult>('search-products', 'POST', {}, { query });
  },

  // Taxonomy
  async getCategories(): Promise<{ categories: CMSCategory[] }> {
    return fetchFromCMS<{ categories: CMSCategory[] }>('api-categories');
  },

  // Reviews
  async getReviews(productId: string, limit?: number): Promise<CMSReviewsResponse> {
    const queryParams: Record<string, string> = { product_id: productId };
    if (limit) queryParams.limit = limit.toString();
    return fetchFromCMS<CMSReviewsResponse>('api-reviews', 'GET', queryParams);
  },

  // Site Settings
  async getSiteSettings(): Promise<{ settings: CMSSiteSettings }> {
    return fetchFromCMS<{ settings: CMSSiteSettings }>('api-site-settings');
  },

  // Sites
  async getSites(): Promise<{ sites: CMSSite[] }> {
    return fetchFromCMS<{ sites: CMSSite[] }>('api-sites');
  },

  // Quick Actions
  async getQuickActions(): Promise<{ quickActions: import("@/types/cms").CMSQuickAction[] }> {
    return fetchFromCMS<{ quickActions: import("@/types/cms").CMSQuickAction[] }>('api-quick-actions');
  },

  // Note: Brands are derived from products in useBrands hook
  // CMS doesn't have a separate api-brands endpoint

  // Get multiple supplements by IDs
  async getSupplementsByIds(ids: string[]): Promise<CMSProductsResponse> {
    if (ids.length === 0) {
      return { 
        products: [], 
        pagination: { page: 1, limit: 0, total: 0, totalPages: 0 } 
      };
    }
    
    const response = await fetchFromCMS<{ supplements: any[], pagination: any }>('api-supplements', 'GET', { 
      ids: ids.join(',') 
    });
    
    // Transform 'supplements' key to 'products' for backwards compatibility
    return {
      products: response.supplements || [],
      pagination: response.pagination
    };
  },

  // Backwards-compatible aliases
  getProducts: function(params?: { page?: number; limit?: number; category?: string; search?: string; }) {
    return this.getSupplements(params);
  },
  getProduct: function(slug: string) {
    return this.getSupplement(slug);
  },
  getProductsByIds: function(ids: string[]) {
    return this.getSupplementsByIds(ids);
  },
};
